import React, { useEffect, useState } from "react";
import "./App.css";
import PrivacyPolicy from "./PrivacyPolicy";
import projectLerni from "./assets/project-lerni.png";
import placeholder from "./assets/placeholder.png";

const sections = [
  { id: "cogi", theme: "paper" },
  { id: "what-are-we", theme: "cyan" },
  { id: "our-work", theme: "magenta" },
  { id: "contact", theme: "yellow" },
];

const themeColors = {
  paper: "#f5f5f5",
  cyan: "#00e5e8",
  magenta: "#ff3cc7",
  yellow: "#f0f600",
};

const normalizePath = (value) => {
  const normalized = (value || "").replace(/\/+$/, "");
  return normalized || "/";
};

const isExternalHref = (href) => /^https?:\/\//i.test(href);

const projects = [
  {
    id: "lerni",
    title: "Lerni",
    blurb: "Award winning VBE national exam preparation app with integrated AI tutor.",
    href: "https://lerni.lt/",
    image: projectLerni,
  },
  {
    id: "placeholder-1",
    title: "WIP",
    blurb: "Work in progress... See you soon...",
    href: "#our-work",
    image: placeholder,
  },
  {
    id: "placeholder-2",
    title: "WIP",
    blurb: "Work in progress... Someday, sometime...",
    href: "#our-work",
    image: placeholder,
  },
];

function App() {
  const currentYear = new Date().getFullYear();
  const isPrivacyPolicyPage =
    normalizePath(window.location.pathname) ===
    normalizePath(`${process.env.PUBLIC_URL || ""}/privacy-policy`);

  const [showHeader, setShowHeader] = useState(false);
  const [headerTheme, setHeaderTheme] = useState("paper");
  const [headerBaseTheme, setHeaderBaseTheme] = useState("paper");
  const [headerAnimKey, setHeaderAnimKey] = useState(0);
  const [activeProject, setActiveProject] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState({
    type: "idle",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const name = formData.name.trim();
    const email = formData.email.trim();
    const message = formData.message.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!name) {
      return "ERROR: no name";
    }

    if (name.length < 2) {
      return "ERROR: name too short";
    }

    if (!email) {
      return "ERROR: no email";
    }

    if (!emailPattern.test(email)) {
      return "ERROR: bad email";
    }

    if (!message) {
      return "ERROR: no message";
    }

    if (message.length < 10) {
      return "ERROR: message too short";
    }

    return null;
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    setFormStatus((current) => (
      current.type === "error"
        ? {
            type: "idle",
            message: "",
          }
        : current
    ));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setFormStatus({
        type: "error",
        message: validationError,
      });
      return;
    }

    setIsSubmitting(true);
    setFormStatus({
      type: "idle",
      message: "",
    });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const responseText = await response.text();
      const contentType = response.headers.get("content-type") || "";
      let payload = {};

      if (responseText && contentType.includes("application/json")) {
        try {
          payload = JSON.parse(responseText);
        } catch {
          throw new Error("ERROR: server returned an invalid response");
        }
      }

      if (!response.ok) {
        throw new Error(
          payload.error ||
            (responseText.startsWith("Proxy error")
              ? "ERROR: contact server is not running on port 3002"
              : responseText) ||
            "ERROR: failed to send the message"
        );
      }

      setFormData({
        name: "",
        email: "",
        message: "",
      });
      setFormStatus({
        type: "success",
        message: payload.message || "Message sent successfully.",
      });
    } catch (error) {
      setFormStatus({
        type: "error",
        message: error.message || "Failed to send the message.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isPrivacyPolicyPage) {
      return undefined;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const headerVisible = scrollY > viewportHeight * 0.2;
      const headerHeight = headerVisible ? 64 : 0;
      const headerTouchY = scrollY + headerHeight;

      const sectionPoints = sections.map((section) => {
        const element = document.getElementById(section.id);
        const threshold = element ? element.offsetTop : 0;

        return { ...section, threshold };
      });

      let activeIndex = 0;
      for (let index = 0; index < sectionPoints.length; index += 1) {
        if (headerTouchY >= sectionPoints[index].threshold) {
          activeIndex = index;
        }
      }

      const currentSection = sectionPoints[activeIndex];
      setShowHeader(headerVisible);

      setHeaderTheme((previousTheme) => {
        if (previousTheme !== currentSection.theme) {
          setHeaderBaseTheme(previousTheme);
          setHeaderAnimKey((value) => value + 1);
          return currentSection.theme;
        }

        return previousTheme;
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isPrivacyPolicyPage]);

  if (isPrivacyPolicyPage) {
    return <PrivacyPolicy />;
  }

  return (
    <main className="landing-container">
      <header
        className={`site-header ${showHeader ? "site-header-visible" : ""}`}
        style={{
          "--header-base": themeColors[headerBaseTheme],
          "--header-next": themeColors[headerTheme],
        }}
      >
        <span
          key={headerAnimKey}
          className="site-header-overlay"
          aria-hidden="true"
        />
        <a className="header-brand" href="#cogi">
          COGI
        </a>
        <nav className="header-nav" aria-label="Primary">
          <a href="#what-are-we">What are we?</a>
          <a href="#our-work">Our work</a>
          <a href="#contact">Contact us!</a>
        </nav>
      </header>

      <section className="hero-panel" id="cogi">
        <div className="panel-shell">
          <div className="hero-copy">
            <h1 className={`wordmark ${showHeader ? "wordmark-hidden" : ""}`}>COGI</h1>
          </div>
          <div className="rail-stack" aria-hidden="true">
            <span className="rail rail-cyan" />
            <span className="rail rail-magenta" />
            <span className="rail rail-yellow" />
          </div>
        </div>
      </section>

      <section className="content-section section-cyan" id="what-are-we">
        <div className="panel-shell">
          <div className="section-inner">
            <p className="section-index">01</p>
            <h2>What are we?</h2>
            <p>
              COGI is a team of two engineers eager to build, create and experiment. We like testing ideas, pushing boundaries and transforming concepts into funcitonal projects.
            </p>
          </div>
        </div>
      </section>

      <section className="content-section section-magenta" id="our-work">
        <div className="panel-shell panel-shell-work">
          <div className="section-inner">
            <p className="section-index">02</p>
            <h2>Our work</h2>
            <p>
              Seeing is believing. Take a look at our past projects and see how they came along.
            </p>
          </div>
          <div className="work-gallery">
            <a
              className={`project-card ${projects[activeProject].className}`}
              href={projects[activeProject].href}
              target={isExternalHref(projects[activeProject].href) ? "_blank" : undefined}
              rel={isExternalHref(projects[activeProject].href) ? "noopener noreferrer" : undefined}
              style={{ backgroundImage: `url(${projects[activeProject].image})` }}
            >
              <strong>{projects[activeProject].title}</strong>
              <span>{projects[activeProject].blurb}</span>
            </a>
            <div className="project-thumbs" aria-label="Project slideshow">
              {projects.map((project, index) => (
                <button
                  key={project.id}
                  type="button"
                  className={`project-thumb ${project.className} ${
                    activeProject === index ? "project-thumb-active" : ""
                  }`}
                  onClick={() => setActiveProject(index)}
                  aria-label={`Show ${project.title}`}
                  style={{ backgroundImage: `url(${project.image})` }}
                >
                  <span>{project.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="content-section section-yellow" id="contact">
        <div className="panel-shell panel-shell-contact">
          <div className="section-inner">
            <p className="section-index">03</p>
            <h2>Contact us!</h2>
            <p>
              Like our work? Let's talk. Reach out and we'll get back to you as soon as possible.
            </p>
          </div>
          <form
            className="contact-console"
            onSubmit={handleFormSubmit}
            aria-labelledby="contact-console-title"
            noValidate
          >
            <div className="contact-console-bar">
              <span className="contact-console-app">C:\cogi-tools\message-sender-v1.1.exe</span>
              <div className="contact-console-actions">
                <a
                  className="contact-console-action"
                  href="#contact"
                  aria-label="Minimize message sender"
                >
                  _
                </a>
                <a
                  className="contact-console-action"
                  href="#contact"
                  aria-label="Maximize message sender"
                >
                  []
                </a>
                <a
                  className="contact-console-action contact-console-action-close"
                  href="#contact"
                  aria-label="Close message sender"
                >
                  X
                </a>
              </div>
            </div>
            <div className="contact-console-body">
              <h3 id="contact-console-title">Cogi Message Sender [version 1.1]</h3>
              <p className="contact-console-note">(c) MB Cogi. All rights reserved.</p>
              <span className="contact-console-spacer" aria-hidden="true" />
              <label className="console-field">
                <span>Name:</span>
                <input
                  type="text"
                  name="name"
                  placeholder="Test User"
                  value={formData.name}
                  onChange={handleFormChange}
                  autoComplete="name"
                />
              </label>
              <label className="console-field">
                <span>Email:</span>
                <input
                  type="email"
                  name="email"
                  placeholder="user@email.com"
                  value={formData.email}
                  onChange={handleFormChange}
                  autoComplete="email"
                />
              </label>
              <label className="console-field console-field-message">
                <span>Message:</span>
                <textarea
                  name="message"
                  placeholder="An important message to the Cogi team."
                  maxLength={5000}
                  rows="6"
                  value={formData.message}
                  onChange={handleFormChange}
                />
              </label>
              <div className="console-actions-row">
                <button className="console-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "> Sending..." : "> Send"}
                </button>
                {formStatus.message ? (
                  <p
                    className={`console-status console-status-${formStatus.type}`}
                    role="status"
                  >
                    {formStatus.message}
                  </p>
                ) : null}
              </div>
            </div>
          </form>
        </div>
      </section>

      <footer className="site-footer">
        <div className="footer-content">
          <p className="footer-phrase">
            <span className="footer-phrase-cyan">colon</span>{", "}
            <span className="footer-phrase-magenta">caret</span>{", "}
            <span className="footer-phrase-yellow">right parenthesis</span>{"."}
          </p>
          <p className="footer-meta">
            &copy; {currentYear} MB Cogi. All rights reserved.{" "}
            <a
              className="footer-privacy-button"
              href={`${process.env.PUBLIC_URL}/privacy-policy`}
            >
              Privacy policy
            </a>
          </p>
        </div>
        <div className="rail-stack rail-stack-footer" aria-hidden="true">
          <span className="rail rail-transparent" />
          <span className="rail rail-transparent" />
          <span className="rail rail-transparent" />
        </div>
      </footer>
    </main>
  );
}

export default App;
